import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'pill' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-ink hover:opacity-90 rounded-card',
  pill: 'bg-accent text-accent-ink hover:opacity-90 rounded-pill',
  secondary: 'bg-surface text-ink border border-line hover:bg-surface-alt rounded-pill',
  ghost: 'text-ink hover:bg-surface-alt rounded-pill',
  danger: 'bg-danger text-accent-ink hover:opacity-90 rounded-pill',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-base gap-2',
  lg: 'h-12 px-6 text-lg gap-2',
};

const BASE =
  'inline-flex select-none items-center justify-center font-medium transition-colors ' +
  'disabled:pointer-events-none disabled:opacity-50';

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  children: ReactNode;
  className?: string;
}

type ButtonProps = CommonProps & ButtonHTMLAttributes<HTMLButtonElement>;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'pill', size = 'md', loading = false, fullWidth, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      // Un botón sin `type` dentro de un form envía el form sin querer.
      type={rest.type ?? 'button'}
      aria-busy={loading || undefined}
      disabled={rest.disabled || loading}
      className={cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
});

interface ButtonLinkProps extends CommonProps {
  to: string;
  'aria-label'?: string;
}

/** Misma apariencia que Button, pero navega: mantiene la semántica de link. */
export function ButtonLink({
  to,
  variant = 'pill',
  size = 'md',
  fullWidth,
  className,
  children,
  ...rest
}: ButtonLinkProps) {
  return (
    <Link
      to={to}
      className={cn(BASE, VARIANTS[variant], SIZES[size], fullWidth && 'w-full', className)}
      {...rest}
    >
      {children}
    </Link>
  );
}

function Spinner() {
  return (
    <svg
      className="h-4 w-4 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
